import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import polyline from '@mapbox/polyline';
import { MapContainer, TileLayer, Polyline } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { fetchWithAuth } from '../utils/api';
import { sportTypeToIcon } from '../utils/sportTypeToIcon';
import SportTypeDropdown from './SportTypeDropdown';
import LoadingIndicator from './LoadingIndicator';

const MAX_DISTANCE_FILTER_ALLOWED = 80000; // 80km in meters

const ActivityList: React.FC<{ athlete: any }> = ({ athlete }) => {
    const [activities, setActivities] = useState<any[]>([]);
    const [selectedActivities, setSelectedActivities] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(false); // State for loading modal
    const [modalContent, setModalContent] = useState<React.ReactNode>(null); // State for modal message
    const [activePopoverId, setActivePopoverId] = useState<string | null>(null); // Track which activity popover is open
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [popoverButtonPressedId, setPopoverButtonPressedId] = useState<string | null>(null); // Track pressed state for button
    const [showCombineMode, setShowCombineMode] = useState(false); // Track if combine mode is active
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [isLoadingNextPage, setIsLoadingNextPage] = useState(false);
    const [roundingActivity, setRoundingActivity] = useState<any | null>(null); // Track activity being rounded
    const [roundingDirection, setRoundingDirection] = useState<'up' | 'down' | null>(null); // Track rounding direction
    const [showDistanceFilter, setShowDistanceFilter] = useState(false); // Track if distance filter modal is open
    const [searchParams, setSearchParams] = useSearchParams();
    const [filters, setFilters] = useState<{ sportType: string; minDistance: number; maxDistance: number }>(() => ({
        sportType: searchParams.get('sportType') || 'All',
        minDistance: Number(searchParams.get('minDistance')) || 0,
        maxDistance: searchParams.get('maxDistance') ? Number(searchParams.get('maxDistance')) : MAX_DISTANCE_FILTER_ALLOWED,
    }));
    const [isLoadingActivities, setIsLoadingActivities] = useState(false);
    const sentinelRef = useRef<HTMLDivElement>(null);
    const isFirstRender = useRef(true);
    const prevSportType = useRef('All'); // To remember previous sport type filter when entering/exiting combine mode

    const buildUrl = (p: number, f: typeof filters) => {
        const params = new URLSearchParams({ page: String(p) });
        if (f.sportType !== 'All') params.set('sportType', f.sportType);
        if (f.minDistance > 0) params.set('minDistance', String(f.minDistance));
        if (f.maxDistance < MAX_DISTANCE_FILTER_ALLOWED) params.set('maxDistance', String(f.maxDistance));
        return `/activities?${params}`;
    };

    const fetchPage = useCallback(async (p: number, f: typeof filters, replace: boolean) => {
        if (replace) {
            setIsLoadingActivities(true);
        } else {
            setIsLoadingNextPage(true);
        }
        try {
            const data = await fetchWithAuth(buildUrl(p, f));
            if (data.activities && data.activities.length > 0) {
                setActivities(prev => replace ? data.activities : [...prev, ...data.activities]);
                setPage(p);
                setHasMore(data.hasMore !== false);
            } else {
                if (replace) setActivities([]);
                setHasMore(false);
            }
        } catch (error) {
            console.error('Error fetching activities:', error);
        } finally {
            setIsLoadingActivities(false);
            setIsLoadingNextPage(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Initial load
    useEffect(() => {
        fetchPage(1, filters, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Re-fetch from page 1 when filters change — skipped on the initial render
    // to avoid double-fetching alongside the initial load effect above.
    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        fetchPage(1, filters, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters.sportType, filters.minDistance, filters.maxDistance]);

    // Keep filters reflected in the URL so links/refreshes stay on the right view.
    useEffect(() => {
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            if (filters.sportType === 'All') next.delete('sportType');
            else next.set('sportType', filters.sportType);
            if (filters.minDistance > 0) next.set('minDistance', String(filters.minDistance));
            else next.delete('minDistance');
            if (filters.maxDistance < MAX_DISTANCE_FILTER_ALLOWED) next.set('maxDistance', String(filters.maxDistance));
            else next.delete('maxDistance');
            return next;
        }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters.sportType, filters.minDistance, filters.maxDistance]);

    const loadNextPage = useCallback(() => {
        // Guard against isLoadingActivities too — the sentinel can already be in
        // view (e.g. on an empty/short list) before the initial page finishes loading.
        if (!isLoadingNextPage && !isLoadingActivities && hasMore) {
            fetchPage(page + 1, filters, false);
        }
    }, [isLoadingNextPage, isLoadingActivities, hasMore, page, filters, fetchPage]);

    // Stable ref so the IntersectionObserver closure always calls the latest
    // loadNextPage without needing to be recreated on every state change.
    const loadNextPageRef = useRef(loadNextPage);
    loadNextPageRef.current = loadNextPage;

    // Infinite scroll — observer is set up once and never torn down.
    useEffect(() => {
        const sentinel = sentinelRef.current;
        if (!sentinel) return;
        const observer = new IntersectionObserver(
            entries => { if (entries[0].isIntersecting) loadNextPageRef.current(); },
            { threshold: 0.1 },
        );
        observer.observe(sentinel);
        return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const reloadActivities = () => fetchPage(1, filters, true);

    const supportsCombineMode = (activity: any) => {
        return (
            !(activity.external_id?.startsWith('streven-cb'))
            && (
                activity.start_latlng &&
                Array.isArray(activity.start_latlng) &&
                activity.start_latlng.length > 0
            )
        );
    }

    const supportsRoundingMode = (activity: any) => {
        return (
            activity.distance && activity.distance >= 1000
            && !activity.external_id?.startsWith('streven-ru')
            && !activity.external_id?.startsWith('streven-rd')
        );
    }

    const getRoundingOptions = (activity: any, direction?: 'up' | 'down' | null) => {
        if (!supportsRoundingMode(activity)) return [];
        
        const distanceInMeters = activity.distance || 0;
        const distanceInKm = distanceInMeters / 1000;
        const options = [];

        // Always available: round to nearest km
        const roundedUp = Math.ceil(distanceInKm);
        const roundedDown = Math.floor(distanceInKm);
        const nearestKm = direction === 'up' ? roundedUp : direction === 'down' ? roundedDown : Math.round(distanceInKm);
        options.push({
            target: 'nearestKm',
            label: `Nearest kilometer (${nearestKm}km)`,
            distance: nearestKm * 1000,
            enabled: true,
            hint: undefined
        });

        // Half marathon: available if within 1km AND can actually round to it in the selected direction
        const halfMarathonKm = 21.1;
        const distToHalfMarathon = Math.abs(distanceInKm - halfMarathonKm);
        const canRoundToHalfMarathon = direction === 'up' 
            ? distanceInKm < halfMarathonKm 
            : direction === 'down' 
            ? distanceInKm > halfMarathonKm 
            : true; // When no direction specified, allow if within range
        options.push({
            target: 'halfMarathon',
            label: 'Half Marathon (21.1km)',
            distance: halfMarathonKm * 1000,
            enabled: distToHalfMarathon <= 1 && canRoundToHalfMarathon,
            hint: !canRoundToHalfMarathon ? `Can't round ${direction} to this` : distToHalfMarathon > 1 ? `${Math.abs(halfMarathonKm - distanceInKm).toFixed(2)}km away` : undefined
        });

        // Marathon: available if within 1km AND can actually round to it in the selected direction
        const marathonKm = 42.2;
        const distToMarathon = Math.abs(distanceInKm - marathonKm);
        const canRoundToMarathon = direction === 'up' 
            ? distanceInKm < marathonKm 
            : direction === 'down' 
            ? distanceInKm > marathonKm 
            : true; // When no direction specified, allow if within range
        options.push({
            target: 'marathon',
            label: 'Marathon (42.2km)',
            distance: marathonKm * 1000,
            enabled: distToMarathon <= 1 && canRoundToMarathon,
            hint: !canRoundToMarathon ? `Can't round ${direction} to this` : distToMarathon > 1 ? `${Math.abs(marathonKm - distanceInKm).toFixed(2)}km away` : undefined
        });

        return options;
    };

    const handleCheckboxChange = (activity: any) => {
        if (selectedActivities.includes(activity)) {
            // Remove activity if already selected
            setSelectedActivities(selectedActivities.filter(existingActivity => existingActivity.id !== activity.id));
        } else if (selectedActivities.length < 2) {
            // Add activity if less than 2 are selected
            setSelectedActivities([...selectedActivities, activity]);
        } else {
            setModalContent(<p>You can only select up to 2 activities.</p>);
        }
    };

    const handleCombineClick = async () => {
        if (selectedActivities.length === 2) {
            console.log('Selected activities:', selectedActivities);
            setIsLoading(true); // Show loading modal
            try {
                const data = await fetchWithAuth('/activities/combine', {
                    method: 'POST',
                    body: JSON.stringify({
                        activities: [
                            {
                                id: selectedActivities[0].id,
                                startDate: selectedActivities[0].start_date,
                                name: selectedActivities[0].name
                            },
                            {
                                id: selectedActivities[1].id,
                                startDate: selectedActivities[1].start_date,
                                name: selectedActivities[1].name
                            }
                        ],
                        athlete: {
                            id: athlete.id,
                            firstName: athlete.firstname,
                        }
                    }),
                });
                if (data.activityId) {
                    setSelectedActivities([]); // Clear selected activities after combining
                    setShowCombineMode(false); // Exit combine mode
                    setFilters(f => ({ ...f, sportType: prevSportType.current })); // Restore previous sport type filter
                    setModalContent(
                        <>
                            <div>
                                <strong>Activities combined successfully!</strong>
                            </div>
                            <div style={{ margin: '12px 0' }}>
                                <a
                                    href={`https://www.strava.com/activities/${data.activityId}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                        backgroundColor: '#FC4C02',
                                        color: 'white',
                                        padding: '10px 20px',
                                        borderRadius: '6px',
                                        textDecoration: 'none',
                                        fontWeight: 600,
                                        marginRight: '8px',
                                        display: 'inline-block'
                                    }}
                                >
                                    View New Activity
                                </a>
                            </div>
                            <div style={{ marginBottom: '8px', fontSize: '15px', color: '#555' }}>
                                <span>Consider deleting the original activities:</span>
                            </div>
                            <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                                {selectedActivities.map(act => (
                                    <a
                                        key={act.id}
                                        href={`https://www.strava.com/activities/${act.id}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{
                                            backgroundColor: 'white',
                                            color: '#FC4C02',
                                            border: '2px solid #FC4C02',
                                            padding: '8px 16px',
                                            borderRadius: '6px',
                                            textDecoration: 'none',
                                            fontWeight: 600,
                                            fontSize: '14px'
                                        }}
                                    >
                                        {act.name || `Activity ${act.id}`}
                                    </a>
                                ))}
                            </div>
                        </>
                    );
                    reloadActivities(); // Reload the activity list
                } else {
                    setModalContent(<p>Failed to combine activities.<br></br>Please try again, if error persists contact us for assistance.</p>);
                }
            } catch (error) {
                console.error('Error combining activities:', error);
                setModalContent(<p>An error occurred while combining activities.</p>);
            } finally {
                setIsLoading(false); // Hide loading modal
            }
        } else {
            setModalContent(<p>Please select exactly 2 activities to combine.</p>);
        }
    };

    const handleRound = async (activity: any, targetDistance: number) => {
        setIsLoading(true);
        const isRoundingUp = targetDistance > activity.distance;
        const endpoint = isRoundingUp ? '/activities/roundup' : '/activities/rounddown';
        
        try {
            const data = await fetchWithAuth(endpoint, {
                method: 'POST',
                body: JSON.stringify({
                    activity: {
                        id: activity.id,
                        startDate: activity.start_date,
                        name: activity.name,
                        distance: activity.distance,
                        sport_type: activity.sport_type,
                        targetDistance: targetDistance
                    },
                    athlete: {
                        id: athlete.id,
                        firstName: athlete.firstname,
                    }
                }),
            });
            setModalContent(
                <>
                    <div>
                        <strong>Activity with updated distance created!</strong>
                    </div>
                    <div style={{ margin: '12px 0' }}>
                        <a
                            href={`https://www.strava.com/activities/${data.activityId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                                backgroundColor: '#FC4C02',
                                color: 'white',
                                padding: '10px 20px',
                                borderRadius: '6px',
                                textDecoration: 'none',
                                fontWeight: 600,
                                marginRight: '8px',
                                display: 'inline-block'
                            }}
                        >
                            View New Activity
                        </a>
                    </div>
                    <div style={{ marginBottom: '8px', fontSize: '15px', color: '#555' }}>
                        <span>Consider deleting the original activity:</span>
                    </div>
                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                        <a
                            key={activity.id}
                            href={`https://www.strava.com/activities/${activity.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                                backgroundColor: 'white',
                                color: '#FC4C02',
                                border: '2px solid #FC4C02',
                                padding: '8px 16px',
                                borderRadius: '6px',
                                textDecoration: 'none',
                                fontWeight: 600,
                                fontSize: '14px'
                            }}
                        >
                            {activity.name || `Activity ${activity.id}`}
                        </a>
                    </div>
                </>
            );
            reloadActivities();
        } catch (error) {
            console.error('Error rounding activity:', error);
            setModalContent(<p>An error occurred while rounding.<br></br>Please try again, if error persists contact us for assistance.</p>);
        } finally {
            setIsLoading(false);
        }
    };

    const closeModal = () => {
        setModalContent(null); // Close the modal
    };

    return (
        <div
            style={{
                maxHeight: '100svh',
                overflowY: 'auto',
                width: '100vw',
                padding: '4px 10px 10px',
                borderBottom: '1px solid #ddd',
                borderRadius: '8px',
                boxSizing: 'border-box',
            }}
            onClick={() => {
                setActivePopoverId(null);
                setPopoverButtonPressedId(null);
            }} // Close popover and button pressed when clicking outside
        >
            <ul
                style={{
                    listStyleType: 'none',
                    padding: 0,
                    margin: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center', // Center the activity cards
                    minHeight: '100svh',
                }}
            >
                {/* Filter Bar */}
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10, gap: 10, flexWrap: 'wrap', justifyContent: 'center' }}>
                    <SportTypeDropdown
                        value={filters.sportType}
                        onChange={val => setFilters(f => ({ ...f, sportType: val }))}
                    />
                    {/* Distance filter button */}
                    <button
                        onClick={() => setShowDistanceFilter(!showDistanceFilter)}
                        style={{
                            padding: '8px 12px',
                            borderRadius: 8,
                            border: '1.5px solid #FC4C02',
                            background: 'white',
                            fontSize: 16,
                            fontWeight: 500,
                            cursor: 'pointer',
                            color: '#222',
                            minHeight: 45,
                            minWidth: 100,
                        }}
                    >
                        {filters.minDistance === 0 && filters.maxDistance === MAX_DISTANCE_FILTER_ALLOWED
                            ? 'Any Distance' // No filter
                            : filters.minDistance > 0 && filters.maxDistance === MAX_DISTANCE_FILTER_ALLOWED
                            ? `Distance >${filters.minDistance / 1000}km` // Min only
                            : filters.minDistance === 0 && filters.maxDistance < MAX_DISTANCE_FILTER_ALLOWED
                            ? `Distance <${filters.maxDistance / 1000}km` // Max only
                            : `Distance ${filters.minDistance / 1000}km - ${filters.maxDistance >= MAX_DISTANCE_FILTER_ALLOWED ? '>80km' : `${filters.maxDistance / 1000}km`}` // Range
                        }
                    </button>
                </div>
                {activities.length === 0 && isLoadingActivities && (
                    <LoadingIndicator message="Loading your activities..." />
                )}
                {activities.length === 0 && !isLoadingActivities && (
                    <div style={{ marginTop: 20, marginBottom: 20, color: '#555', fontSize: '1.1em' }}>
                        No activities found for the selected filter.
                    </div>
                )}
                {activities.map((activity: any) => {
                    let distanceKm = '-';
                    let durationStr = '-';
                    let paceStr = '-';
                    if (activity.distance && activity.elapsed_time) {
                        // Convert from meters to kilometers (e.g. 6707.6m -> 6.7076km)
                        const km = (activity.distance || 0) / 1000;
                        // Floor to 2 decimals (e.g. 6.7076 -> 6.70)
                        // Strava does not round up distances
                        distanceKm = (Math.floor(km * 100) / 100).toFixed(2);
                        const durationSec = activity.moving_time || activity.elapsed_time || 0;
                        const durationMin = Math.floor(durationSec / 60);
                        const durationSecRemainder = durationSec % 60;
                        durationStr = `${durationMin}m ${durationSecRemainder.toString().padStart(2, '0')}s`;

                        // Calculate pace (min/km)
                        if (activity.distance > 0 && durationSec > 0) {
                            const pace = durationSec / (activity.distance / 1000); // seconds per km
                            const paceMin = Math.floor(pace / 60);
                            const paceSec = Math.round(pace % 60);
                            paceStr = `${paceMin}:${paceSec.toString().padStart(2, '0')} /km`;
                        }
                    }
                    return (

                        <li
                            key={activity.id}
                            style={{
                                position: 'relative', // Add relative positioning for badge placement
                                marginBottom: '20px',
                                padding: '10px',
                                border: '1px solid #ddd',
                                borderRadius: '8px',
                                boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
                                width: '90%', // Make the activity cards take up 90% of the screen width
                                maxWidth: '600px', // Limit the maximum width of the cards
                                backgroundColor: '#fff', // Add a white background for better contrast
                            }}
                        >
                            {/* Popover for this activity */}
                            {activePopoverId === activity.id && (
                                <div
                                    style={{
                                        position: 'absolute',
                                        top: '50px',
                                        right: '10px',
                                        background: 'white',
                                        border: '1px solid #ccc',
                                        borderRadius: '8px',
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                        zIndex: 3000,
                                        padding: '20px 16px',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '12px',
                                        minWidth: '120px',
                                        alignItems: 'stretch',
                                    }}
                                    onClick={e => e.stopPropagation()} // Prevent closing when clicking inside
                                >
                                    {supportsCombineMode(activity) && (<button
                                        style={{ padding: '10px', borderRadius: '6px', border: 'none', background: '#FC4C02', color: 'white', fontWeight: 600, cursor: 'pointer' }}
                                        onClick={() => {
                                            // Enter combine mode and filter the list to this activity's sport type
                                            setShowCombineMode(true);
                                            setActivePopoverId(null);
                                            // Set the sport type filter so only same-sport activities are shown
                                            prevSportType.current = filters.sportType;
                                            setFilters(f => ({ ...f, sportType: activity.sport_type || 'All' }));
                                            if (!selectedActivities.includes(activity) && supportsCombineMode(activity)) {
                                                setSelectedActivities([activity]);
                                            }
                                        }}
                                    >
                                        Combine
                                    </button>)}
                                    {supportsRoundingMode(activity) && (
                                        <>
                                            <button
                                                style={{ padding: '10px', borderRadius: '6px', border: 'none', background: 'blue', color: 'white', fontWeight: 600, cursor: 'pointer' }}
                                                onClick={() => {
                                                    setRoundingActivity(activity);
                                                    setRoundingDirection('up');
                                                    setActivePopoverId(null);
                                                }}
                                            >
                                                Round Up
                                            </button>
                                            <button
                                                style={{ padding: '10px', borderRadius: '6px', border: 'none', background: 'grey', color: 'white', fontWeight: 600, cursor: 'pointer' }}
                                                onClick={() => {
                                                    setRoundingActivity(activity);
                                                    setRoundingDirection('down');
                                                    setActivePopoverId(null);
                                                }}
                                            >
                                                Round Down
                                            </button>
                                        </>
                                    )}
                                </div>
                            )}
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                {/* Only show checkboxes in combine mode */}
                                {showCombineMode && supportsCombineMode(activity) && (
                                    <input
                                        type="checkbox"
                                        checked={selectedActivities.includes(activity)}
                                        onChange={() => handleCheckboxChange(activity)}
                                        style={{ marginRight: '10px', height: '20px', width: '20px' }}
                                    />
                                )}
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '8px 0', paddingRight: '5px' }}>
                                    {sportTypeToIcon(activity.sport_type)}
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', paddingBottom: '10px', width: '100%' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                        <h3 style={{ margin: 0 }}>{activity.name}</h3>
                                    </div>
                                    <div style={{ fontSize: '0.95em', color: '#555', marginTop: 2 }}>
                                        {new Date(activity.start_date_local.replace(/Z$/, '')).toLocaleString()} · {activity.device_name || 'Unknown Device'}
                                    </div>
                                </div>
                                {(activity.external_id?.startsWith('streven-')) && (
                                    <div
                                        style={{
                                            width: '130px',
                                            backgroundColor: 'blue',
                                            color: 'white',
                                            padding: '5px 10px',
                                            borderRadius: '5px',
                                            fontSize: '12px',
                                            fontWeight: 'bold',
                                            marginRight: '5px',
                                            textAlign: 'center',
                                        }}
                                    >
                                        {activity.external_id?.startsWith('streven-cb-') && 'Combined'}
                                        {activity.external_id?.startsWith('streven-ru') && 'Rounded Up'}
                                        {activity.external_id?.startsWith('streven-rd') && 'Rounded Down'}
                                    </div>
                                )}
                                {/* Popover trigger button in top right */}
                                <button
                                    style={{
                                        background: activePopoverId === activity.id ? '#ddd' : '#eee',
                                        border: 'none',
                                        borderRadius: '8px',
                                        width: '32px',
                                        height: '32px',
                                        cursor: 'pointer',
                                        zIndex: 10,
                                        boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
                                        transition: 'background 0.2s',
                                    }}
                                    aria-label="Show activity actions"
                                    onClick={e => {
                                        e.stopPropagation();
                                        if (activePopoverId === activity.id) {
                                            setActivePopoverId(null);
                                            setPopoverButtonPressedId(null);
                                        } else {
                                            setActivePopoverId(activity.id);
                                            setPopoverButtonPressedId(activity.id);
                                        }
                                    }}
                                    onMouseDown={() => setPopoverButtonPressedId(activity.id)}
                                    onMouseUp={() => setPopoverButtonPressedId(null)}
                                >
                                    <span style={{ fontSize: '20px', color: '#555' }}>⋮</span>
                                </button>

                            </div>
                            {activity.start_latlng && Array.isArray(activity.start_latlng) && activity.start_latlng.length > 1 && (
                                <>
                                    {/* Stats row */}
                                    <div style={{
                                        display: 'flex', gap: 32, marginBottom: 12, justifyContent: 'center', // Center the stats row horizontally
                                        width: '100%',
                                    }}>
                                        <div style={{ textAlign: 'center' }}>
                                            <div style={{ fontSize: '1.3em' }}>{distanceKm} <span style={{ fontWeight: 400, fontSize: '0.8em' }}>km</span></div>
                                            <div style={{ color: '#888', fontSize: '0.9em' }}>Distance</div>
                                        </div>
                                        <div style={{ textAlign: 'center' }}>
                                            <div style={{ fontSize: '1.3em' }}>{paceStr}</div>
                                            <div style={{ color: '#888', fontSize: '0.9em' }}>Pace</div>
                                        </div>
                                        <div style={{ textAlign: 'center' }}>
                                            <div style={{ fontSize: '1.3em' }}>{durationStr}</div>
                                            <div style={{ color: '#888', fontSize: '0.9em' }}>Time</div>
                                        </div>
                                    </div>
                                    {(() => {
                                        const routePositions: [number, number][] = activity.map?.summary_polyline
                                            ? polyline.decode(activity.map.summary_polyline).map(([lat, lng]: [number, number]) => [lat, lng])
                                            : [[activity.start_latlng[0], activity.start_latlng[1]]];
                                        return (
                                            <div style={{ height: '300px', marginTop: '10px' }}>
                                                <MapContainer
                                                    // @ts-ignore
                                                    bounds={routePositions}
                                                    boundsOptions={{ padding: [20, 20] }}
                                                    style={{ height: '100%', width: '100%', borderRadius: '8px' }}
                                                    // Disable map interactions for better UX on mobile, in future we can allow map interactions when viewing activity details
                                                    // @ts-ignore
                                                    zoomControl={false}
                                                    dragging={false}
                                                    touchZoom={false}
                                                    scrollWheelZoom={false}
                                                    doubleClickZoom={false}
                                                    boxZoom={false}
                                                    keyboard={false}
                                                >
                                                    <TileLayer
                                                        url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
                                                        // @ts-ignore
                                                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors' />
                                                    <Polyline
                                                        // @ts-ignore
                                                        positions={routePositions}
                                                        // @ts-ignore
                                                        color="blue" />
                                                </MapContainer>
                                            </div>
                                        );
                                    })()}
                                </>
                            )}
                            <div style={{ marginTop: '10px', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <a
                                    href={`https://www.strava.com/activities/${activity.id}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                        color: '#FC4C02',
                                        textDecoration: 'underline',
                                        fontWeight: 'bold',
                                        fontSize: '0.75em',
                                        background: 'none',
                                        border: 'none',
                                        padding: 0,
                                        boxShadow: 'none',
                                        cursor: 'pointer',
                                    }}
                                >
                                    View on Strava
                                </a>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.95em', color: '#555', fontWeight: 500 }}>
                                    {activity.visibility === 'everyone' && (
                                        <>
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" >
                                                <circle cx="12" cy="12" r="10" stroke="#888" strokeWidth="2" fill="none" />
                                                <ellipse cx="12" cy="12" rx="6" ry="10" stroke="#888" strokeWidth="2" fill="none" />
                                                <ellipse cx="12" cy="12" rx="10" ry="4" stroke="#888" strokeWidth="2" fill="none" />
                                            </svg>
                                            Everyone
                                        </>
                                    )}
                                    {activity.visibility === 'only_me' && (
                                        <>
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" ><rect x="5" y="11" width="14" height="8" rx="2" stroke="#888" strokeWidth="2" fill="none" /><path d="M8 11V8a4 4 0 1 1 8 0v3" stroke="#888" strokeWidth="2" fill="none" /></svg>
                                            Only me
                                        </>
                                    )}
                                    {activity.visibility === 'followers_only' && (
                                        <>
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" ><circle cx="8" cy="10" r="3" stroke="#888" strokeWidth="2" fill="none" /><circle cx="16" cy="10" r="3" stroke="#888" strokeWidth="2" fill="none" /><path d="M2 20c0-3.3137 3.134-6 7-6s7 2.6863 7 6" stroke="#888" strokeWidth="2" fill="none" /><path d="M14 20c0-2.2091 2.239-4 5-4s5 1.7909 5 4" stroke="#888" strokeWidth="2" fill="none" /></svg>
                                            Followers Only
                                        </>
                                    )}
                                </div>
                            </div>
                        </li>
                    )
                }
                )}
                {isLoadingNextPage && (
                    <LoadingIndicator message="Loading more..." size={22} padding="10px 0" />
                )}
                <div ref={sentinelRef} style={{ height: 1 }} />
            </ul>
            {/* Show combine button only in combine mode */}
            {showCombineMode && (
                <div
                    style={{
                        position: 'fixed',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        // Use env(safe-area-inset-bottom) for iOS safe area, and fallback to 0 if not supported, plus extra space for Android bottom bar
                        bottom: 'calc(env(safe-area-inset-bottom, 0px) + 24px)',
                        display: 'flex',
                        justifyContent: 'center',
                        zIndex: 1000,
                        gap: '16px', // Add gap between buttons
                        pointerEvents: 'none', // Prevents blocking mouse actions in the same area
                        // Add padding for safe area on iOS Safari (so we don't get hidden by bottom bar) and extra for Android bottom bar
                        paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 24px)',
                        width: 'auto',
                    }}
                >
                    <button
                        onClick={handleCombineClick}
                        disabled={selectedActivities.length !== 2}
                        style={{
                            backgroundColor: selectedActivities.length === 2 ? '#FC4C02' : 'grey', // Orange when active
                            color: 'white',
                            border: 'none',
                            padding: '15px 30px',
                            fontSize: '18px',
                            cursor: 'pointer',
                            borderRadius: '8px',
                            boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.2)',
                            transition: 'background-color 0.3s ease',
                            pointerEvents: 'auto',  // Allows the button itself to be clickable
                            fontWeight: 600,
                        }}
                        onMouseOver={(e) => (e.currentTarget.style.backgroundColor = selectedActivities.length === 2 ? '#e04a02' : 'grey')}
                        onMouseOut={(e) => (e.currentTarget.style.backgroundColor = selectedActivities.length === 2 ? '#FC4C02' : 'grey')}
                    >
                        Combine Activities
                    </button>
                    <button
                        onClick={() => {
                            setShowCombineMode(false);
                            setFilters(f => ({ ...f, sportType: prevSportType.current })); // Restore previous sport type filter
                            setSelectedActivities([]);
                        }}
                        style={{
                            backgroundColor: 'white',
                            color: 'blue',
                            border: '2px solid blue',
                            padding: '15px 30px',
                            fontSize: '18px',
                            cursor: 'pointer',
                            borderRadius: '8px',
                            boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.08)',
                            pointerEvents: 'auto',
                            fontWeight: 600,
                        }}
                    >
                        Cancel
                    </button>
                </div>
            )}
            {(isLoading || modalContent) && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100vw',
                        height: '100vh',
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        zIndex: 2000,
                    }}
                >
                    <div
                        style={{
                            backgroundColor: 'white',
                            padding: '20px',
                            borderRadius: '8px',
                            boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.2)',
                            textAlign: 'center',
                        }}
                    >
                        {isLoading ? (
                            <p style={{ fontSize: '18px', margin: 0 }}>Creating updated activity, please wait...</p>
                        ) : (
                            <>
                                {modalContent}
                                <button
                                    onClick={closeModal}
                                    style={{
                                        marginTop: '10px',
                                        backgroundColor: 'blue',
                                        color: 'white',
                                        border: 'none',
                                        padding: '10px 20px',
                                        fontSize: '16px',
                                        cursor: 'pointer',
                                        borderRadius: '5px',
                                    }}
                                >
                                    Close
                                </button>
                            </>
                        )}
                    </div>
                </div>
            )}
            {roundingActivity && roundingDirection && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100vw',
                        height: '100vh',
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        zIndex: 2001,
                    }}
                    onClick={() => {
                        setRoundingActivity(null);
                        setRoundingDirection(null);
                    }}
                >
                    <div
                        style={{
                            backgroundColor: 'white',
                            padding: '20px',
                            borderRadius: '8px',
                            boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.2)',
                            textAlign: 'center',
                            maxWidth: '400px',
                            width: '90%',
                        }}
                        onClick={e => e.stopPropagation()}
                    >
                        <h2 style={{ margin: '0 0 16px 0', fontSize: '18px' }}>
                            Round {roundingDirection === 'up' ? 'Up' : 'Down'} to:
                        </h2>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            {(() => {
                                const options = getRoundingOptions(roundingActivity, roundingDirection);
                                return options.map((option) => (
                                    <button
                                        key={option.target}
                                        disabled={!option.enabled}
                                        title={option.hint}
                                        style={{
                                            padding: '12px',
                                            borderRadius: '6px',
                                            border: 'none',
                                            background: option.enabled ? '#0b76ff' : '#ccc',
                                            color: option.enabled ? 'white' : '#999',
                                            fontWeight: 600,
                                            cursor: option.enabled ? 'pointer' : 'not-allowed',
                                            fontSize: '14px',
                                            opacity: option.enabled ? 1 : 0.6
                                        }}
                                        onClick={() => {
                                            if (option.enabled) {
                                                handleRound(roundingActivity, option.distance);
                                                setRoundingActivity(null);
                                                setRoundingDirection(null);
                                            }
                                        }}
                                    >
                                        {option.label}
                                    </button>
                                ));
                            })()}
                        </div>
                        <button
                            style={{
                                marginTop: '16px',
                                backgroundColor: 'white',
                                color: '#666',
                                border: '1px solid #ddd',
                                padding: '10px 16px',
                                fontSize: '14px',
                                cursor: 'pointer',
                                borderRadius: '5px',
                            }}
                            onClick={() => {
                                setRoundingActivity(null);
                                setRoundingDirection(null);
                            }}
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            )}
            {/* Distance Filter Modal */}
            {showDistanceFilter && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100vw',
                        height: '100vh',
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        zIndex: 2001,
                    }}
                    onClick={() => setShowDistanceFilter(false)}
                >
                    <div
                        id="distance-filter-modal"
                        style={{
                            backgroundColor: 'white',
                            padding: '20px',
                            borderRadius: '8px',
                            boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.2)',
                            textAlign: 'center',
                            maxWidth: '400px',
                            width: '90%',
                        }}
                        onClick={e => e.stopPropagation()}
                    >
                        <h2 style={{ margin: '0 0 20px 0', fontSize: '18px' }}>Distance Filter</h2>
                        
                        {/* Min Distance Slider */}
                        <div style={{ marginBottom: '20px', textAlign: 'left' }}>
                            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 600 }}>
                                Min Distance: {filters.minDistance / 1000}km
                            </label>
                            <input
                                type="range"
                                min="0"
                                max="80"
                                step="1"
                                value={filters.minDistance / 1000}
                                onChange={(e) => {
                                    const newMin = Number(e.target.value) * 1000;
                                    if (newMin <= filters.maxDistance || filters.maxDistance > MAX_DISTANCE_FILTER_ALLOWED) {
                                        setFilters(f => ({ ...f, minDistance: newMin }));
                                    }
                                }}
                                style={{ width: '100%', cursor: 'pointer', accentColor: '#FC4C02' }}
                            />
                        </div>

                        {/* Max Distance Slider */}
                        <div style={{ marginBottom: '20px', textAlign: 'left' }}>
                            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 600 }}>
                                Max Distance: {filters.maxDistance >= MAX_DISTANCE_FILTER_ALLOWED ? '>80km' : `${filters.maxDistance / 1000}km`}
                            </label>
                            <input
                                type="range"
                                min="0"
                                max="80"
                                step="1"
                                value={Math.min(filters.maxDistance / 1000, 80)}
                                onChange={(e) => {
                                    const newMax = Number(e.target.value) * 1000;
                                    if (newMax >= filters.minDistance) {
                                        setFilters(f => ({ ...f, maxDistance: newMax }));
                                    }
                                }}
                                style={{ width: '100%', cursor: 'pointer', accentColor: '#FC4C02' }}
                            />
                        </div>

                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                            <button
                                onClick={() => setShowDistanceFilter(false)}
                                style={{
                                    flex: 1,
                                    padding: '10px 16px',
                                    backgroundColor: '#FC4C02',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '6px',
                                    fontSize: '14px',
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                }}
                            >
                                Done
                            </button>
                            <button
                                onClick={() => {
                                    setFilters(f => ({ ...f, minDistance: 0, maxDistance: MAX_DISTANCE_FILTER_ALLOWED }));
                                }}
                                style={{
                                    flex: 1,
                                    padding: '10px 16px',
                                    backgroundColor: 'white',
                                    color: '#666',
                                    border: '1px solid #ddd',
                                    borderRadius: '6px',
                                    fontSize: '14px',
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                }}
                            >
                                Reset
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ActivityList;